import styled from "styled-components";

import { pxToRem } from "../../styles/utils";

export const VideoSplitRoot = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns:
    minmax(${pxToRem(420)}, var(--student-video-left-width, 1fr))
    ${pxToRem(16)}
    minmax(${pxToRem(360)}, 1fr);
  column-gap: ${pxToRem(14)};
  align-items: start;

  @media (max-width: 1080px) {
    display: block;
  }
`;

export const VideoSplitContent = styled.div`
  min-width: 0;
`;

export const VideoSplitDivider = styled.div<{ $dragging: boolean }>`
  position: relative;
  min-height: ${pxToRem(420)};
  align-self: stretch;
  cursor: col-resize;
  touch-action: none;
  user-select: none;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: ${({ $dragging }) => pxToRem($dragging ? 3 : 2)};
    border-radius: ${({ theme }) => pxToRem(theme.radii.pill)};
    background: ${({ $dragging, theme }) =>
      $dragging ? theme.colors.brand : theme.borders.divider};
    transform: translateX(-50%);
    transition: width 0.15s ease, background-color 0.15s ease;
  }

  &::after {
    content: "";
    position: sticky;
    top: 46%;
    display: block;
    width: ${pxToRem(8)};
    height: ${pxToRem(42)};
    margin: 0 auto;
    border: ${pxToRem(1)} solid
      ${({ $dragging, theme }) => ($dragging ? theme.colors.brand : theme.borders.outline)};
    border-radius: ${({ theme }) => pxToRem(theme.radii.pill)};
    background: ${({ $dragging, theme }) =>
      $dragging ? theme.colors.brandSubtle : theme.colors.white};
    box-shadow: ${({ theme }) => theme.shadows.sm};
    transition: border-color 0.15s ease, background-color 0.15s ease;
  }

  &:hover::before {
    width: ${pxToRem(3)};
    background: ${({ theme }) => theme.colors.brand};
  }

  &:hover::after,
  &:focus-visible::after {
    border-color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.brandSubtle};
  }

  &:focus-visible {
    outline: none;
  }

  @media (max-width: 1080px) {
    display: none;
  }
`;

export const VideoPreviewPanel = styled.aside`
  position: sticky;
  top: 0;
  min-width: 0;
  overflow: hidden;
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.sm};

  @media (max-width: 1080px) {
    position: relative;
    top: auto;
    margin-top: ${({ theme }) => pxToRem(theme.spacing.lg)};
  }
`;

export const VideoPreviewHeader = styled.div`
  display: flex;
  min-height: ${pxToRem(62)};
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  padding: ${({ theme }) => pxToRem(theme.spacing.sm)} ${({ theme }) => pxToRem(theme.spacing.md)};
  border-bottom: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
`;

export const VideoPreviewFile = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`;

export const VideoPreviewKicker = styled.div`
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.3;
`;

export const VideoPreviewName = styled.div`
  overflow: hidden;
  margin-top: ${pxToRem(2)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 600;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const VideoPreviewActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
`;

export const VideoPreviewOpenLink = styled.a`
  display: inline-flex;
  height: ${pxToRem(34)};
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.brand};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  font-weight: 500;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  svg {
    width: ${pxToRem(14)};
    height: ${pxToRem(14)};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.brandSubtle};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }

  @media (max-width: 1240px) {
    span {
      display: none;
    }
  }
`;

export const VideoPreviewCloseButton = styled.button`
  display: inline-flex;
  width: ${pxToRem(34)};
  height: ${pxToRem(34)};
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.inkSoft};
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;

  svg {
    width: ${pxToRem(14)};
    height: ${pxToRem(14)};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.brandSubtle};
    color: ${({ theme }) => theme.colors.brand};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;

export const VideoPreviewFrameWrap = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  margin: ${({ theme }) => pxToRem(theme.spacing.md)};
  overflow: hidden;
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.inkNearBlack};
`;

export const VideoPreviewFrame = styled.iframe`
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  background: ${({ theme }) => theme.colors.inkNearBlack};
`;

export const VideoPreviewFooter = styled.div`
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.md)} ${({ theme }) => pxToRem(theme.spacing.md)};
`;

export const VideoPreviewCaption = styled.div`
  overflow: hidden;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  font-weight: 500;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const VideoPreviewHelp = styled.div`
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.45;
`;
